<template>
  <h1 class="wordmark" aria-label="Scrawl">
    Scraw<span class="l-c">l</span><span class="dot" aria-hidden="true"></span>
  </h1>
</template>

<script>
export default {
  name: "Wordmark",
};
</script>

<style lang="scss" scoped>
/* ─── Soft Y2K Bubble wordmark ───
   The full word "Scraw" is clipped to the holographic linear gradient
   (pink → purple → cyan → mint → peach), the trailing "l" overrides that
   with a tighter pink-only gradient so it reads as the brand's hero
   glyph, and a small bubblegum bubble (.dot) trails the l like an
   i-tittle. The dot is a solid bubblegum circle with a white inset top
   highlight + a soft pink ambient drop, so it reads as a glossy candy
   bubble.

   The wrapper carries a `filter: drop-shadow(...)` pink halo that the
   gradient text picks up — this is what gives the wordmark its airy,
   floating-over-sky feel.

   `--wordmark-size` drives both the type size and the dot's diameter
   so callers (e.g. Login / SignUp scaling the wordmark to 54px) only
   need to override one custom property.

   Anchored to mocks/redesign-4-soft-y2k-bubble.html screen 01 (lines
   1212 + 373–388): wordmark + .dot styling.
*/
.wordmark {
  --wordmark-size: 64px;
  --dot-size: calc(var(--wordmark-size) * (14 / 64));

  font-family: $font-display;
  font-weight: 800;
  font-size: var(--wordmark-size);
  line-height: 0.85;
  letter-spacing: -0.04em;
  display: inline-block;
  margin: 0;
  padding: 0 6px;
  color: $ink; // fallback for browsers without background-clip

  /* Mock-faithful $holo-linear (mock /mocks/redesign-4-soft-y2k-bubble.html
     lines 373-388). Earlier attempt to darken the gradient drifted too
     far from the mock; legibility is now handled purely through a
     layered drop-shadow halo (a thin deep-purple ground shadow for edge
     contrast + the mock's pink ambient glow), keeping the gradient
     light and on-brand. */
  background: $holo-linear;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;

  filter:
    drop-shadow(0 1px 0 rgba($ink, 0.22))
    drop-shadow(0 4px 12px rgba($bubblegum, 0.45));

  .l-c {
    /* Pink-only gradient override — the "l" reads as a slightly louder
       pink than the holo "Scraw" so it anchors the wordmark. Mock
       inline style (line 1212): linear-gradient(160deg,$bubblegum,$bubblegum-deep). */
    display: inline-block;
    position: relative;
    font-style: normal;
    font-weight: inherit;
    background: linear-gradient(160deg, $bubblegum, $bubblegum-deep);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .dot {
    /* Glossy candy bubble. Solid bubblegum fill — NOT a gradient — with
       a white inset top highlight + a soft pink ambient drop underneath. */
    display: inline-block;
    width: var(--dot-size);
    height: var(--dot-size);
    border-radius: 50%;
    background: $bubblegum;
    margin-left: 2px;
    vertical-align: 8px;
    box-shadow:
      inset 0 2px 0 #fff,
      0 4px 10px -2px rgba($bubblegum, 0.7);
  }
}

/* ─── Responsive sizing ───
   Scale the wordmark down on shorter / narrower viewports. The dot
   tracks size automatically via the `--dot-size` calc, so we only
   override `--wordmark-size`.
*/
@media (max-height: 819px) {
  .wordmark { --wordmark-size: 58px; }
}

@media (max-height: 719px) {
  .wordmark { --wordmark-size: 54px; }
}

@media (max-height: 619px) {
  .wordmark { --wordmark-size: 48px; }
}

@media (max-width: 359px) {
  .wordmark { --wordmark-size: 50px; }
}
</style>
